/**
 * Procedural underwater audio (PRD §28/§29). Everything is synthesised with
 * WebAudio — no assets. The character is low, muffled and spacious: a master
 * low-pass shapes the whole mix, and near-silence is allowed to happen.
 *
 * The one exception is `music.ts`, which plays composed audio. It hangs off the
 * master gain rather than the muffle, because score is over the world and not
 * in it.
 */

import { Music } from './music';

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Where the sub is, musically. Drives the drone bed (PRD §29). */
export type Mood = 'calm' | 'kelp' | 'city' | 'military' | 'vents' | 'abyss' | 'ancient' | 'boss';

/** Everything the player can scoop up. Each gets its own figure in one key. */
export type PowerupKind =
  | 'repair' | 'shield' | 'energy' | 'nitro' | 'torpedo'
  | 'charge' | 'overdrive' | 'aegis' | 'life' | 'salvage' | 'module';

type Six = readonly [number, number, number, number, number, number];

type MoodSpec = {
  /** The six drone partials. The first four are the original bed. */
  freqs: Six;
  /** Cents of detune — the beating between partials is most of the character. */
  detune: Six;
  /** Relative partial levels; 0 parks a voice silently rather than stopping it. */
  levels: Six;
  tone: number;   // music low-pass cutoff
  q: number;      // and its resonance — high Q gives the bed a formant
  wet: number;    // send into the short room reverb
  space: number;  // send into the long tail (caves, cathedrals, the drop)
  bed: number;    // overall drone level
  amb: number;    // and a nudge to the noise ambience under it
  every: number;  // seconds between mood events; 0 = the mood is silent between drones
};

/**
 * The moods. `calm` is deliberately identical to the original bed so anything
 * that never passes a mood sounds exactly as it did. Everything is tuned to A
 * minor, same as the pickups, so a mood change under a pickup never clashes.
 */
const MOODS: Record<Mood, MoodSpec> = {
  // The open sea: root, fifth, octave, one thin harmonic on top.
  calm:     { freqs: [55, 82.5, 110, 164.8, 220, 41.2], detune: [0, -8, 6, 4, -4, 3],
              levels: [1, 1, 1, 0.4, 0, 0], tone: 1400, q: 0.4, wet: 0, space: 0, bed: 1, amb: 1, every: 0 },
  // Forest: warm, open, the fourth softening the fifth.
  kelp:     { freqs: [55, 82.4, 110, 146.8, 220, 41.2], detune: [0, -6, 5, 9, -10, 2],
              levels: [1, 0.9, 0.8, 0.5, 0.3, 0.35], tone: 820, q: 0.5, wet: 0.2, space: 0.12, bed: 1, amb: 1.05, every: 7 },
  // Drowned city: fifths and octaves only, no third — hollow — and a long tail.
  city:     { freqs: [55, 82.4, 110, 164.8, 329.6, 27.5], detune: [0, -4, 7, 3, -9, 0],
              levels: [0.8, 1, 0.6, 0.7, 0.3, 0.45], tone: 1150, q: 0.6, wet: 0.45, space: 0.6, bed: 0.95, amb: 0.95, every: 6 },
  // Garrison: a tritone sitting on the root, dark, with a slow machine pulse.
  military: { freqs: [55, 77.8, 110, 155.6, 207.7, 36.7], detune: [0, 7, -6, 4, 9, 0],
              levels: [1, 0.35, 0.7, 0.28, 0.16, 0.55], tone: 700, q: 0.9, wet: 0.14, space: 0.1, bed: 1.05, amb: 1, every: 1.35 },
  // Vents: detuned hard and filtered low, so it never quite settles.
  vents:    { freqs: [41.2, 55, 61.7, 110, 164.8, 30.9], detune: [0, 11, -14, 6, -8, 4],
              levels: [1, 0.8, 0.5, 0.35, 0.16, 0.65], tone: 430, q: 1.2, wet: 0.18, space: 0.18, bed: 1.1, amb: 1.25, every: 3.5 },
  // The drop: two octaves lower than anything else, mostly empty, huge.
  abyss:    { freqs: [27.5, 41.2, 55, 82.4, 164.8, 32.7], detune: [0, -3, 4, -6, 7, 2],
              levels: [1, 0.5, 0.6, 0.22, 0.08, 0.35], tone: 330, q: 0.5, wet: 0.3, space: 0.8, bed: 0.85, amb: 0.9, every: 11 },
  // Ruins: a clean stacked-fifth chord, wide detune for the shimmer.
  ancient:  { freqs: [55, 82.4, 130.8, 196, 261.6, 43.7], detune: [0, 4, -9, 12, -14, 6],
              levels: [0.7, 0.85, 0.6, 0.5, 0.35, 0.25], tone: 1650, q: 0.7, wet: 0.34, space: 0.55, bed: 0.95, amb: 0.9, every: 8 },
  // Boss: every partial on, a minor third and a major third at once.
  boss:     { freqs: [55, 65.4, 82.4, 110, 138.6, 36.7], detune: [0, -7, 9, -5, 11, 0],
              levels: [1, 0.7, 0.9, 0.8, 0.5, 0.9], tone: 900, q: 0.8, wet: 0.2, space: 0.3, bed: 1.3, amb: 1.05, every: 0.62 },
};

export class Audio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  /** Effects bus. Everything diegetic passes through it on the way to master. */
  private sfxBus!: GainNode;
  /** Player-set bus levels, 0..1. Applied on top of every internal envelope. */
  private vol = { music: 0.8, sfx: 1, amb: 1 };
  /**
   * The score. Created with the context, because there is no context until a
   * gesture. Connected to `master` rather than to `muffle`: the mute already
   * works through master, and score is not in the water.
   */
  music: Music | null = null;
  private muffle!: BiquadFilterNode;
  private reverb!: ConvolverNode;
  private wet!: GainNode;

  private thrusterGain!: GainNode;
  private thrusterFilter!: BiquadFilterNode;
  private ambienceGain!: GainNode;
  private musicGain!: GainNode;
  private musicTone!: BiquadFilterNode;
  private musicWet!: GainNode;
  private spaceWet!: GainNode;
  private musicVoices: { osc: OscillatorNode; gain: GainNode }[] = [];

  // The nitro jet is a loop, not an event, so its nodes are built once and kept.
  private nitroGain: GainNode | null = null;
  private nitroOsc: OscillatorNode | null = null;
  private nitroBand: BiquadFilterNode | null = null;
  private nitroLow: BiquadFilterNode | null = null;
  private nitroOn = false;

  private noiseBuffer: AudioBuffer | null = null;
  private started = false;
  private creakTimer = 4;
  private ambientTimer = 6;
  private mood: Mood = 'calm';
  private moodTimer = 3;
  private lowHullOn = false;
  private lowHullTimer = 0;
  muted = false;

  /**
   * One-shot voices currently wired into the graph, and the high-water mark.
   * On the F3 readout, because "are we disposing of things correctly" is not a
   * question anyone should have to answer by reading the source. A voice that
   * has finished and been torn down decrements this; if the live count climbs
   * while nothing is firing, something is not being released.
   */
  voices = 0;
  voicePeak = 0;

  /** Master level when not muted. One constant, so nothing can disagree. */
  private static readonly LEVEL = 0.85;

  /** True once the context exists and is actually running. */
  get running() {
    return !!this.ctx && this.ctx.state === 'running';
  }

  /**
   * Resume a context that a mobile browser parked. iOS in particular creates
   * the context suspended and only lets a gesture start it, and a context that
   * was running can be suspended again when the app is backgrounded — so this
   * has to be safe to call on every gesture, not once at boot.
   */
  resume() {
    if (this.ctx && this.ctx.state !== 'running') {
      this.ctx.resume().catch(() => { /* another gesture will come */ });
    }
  }

  /**
   * Park the graph while the tab is hidden.
   *
   * Not the same as muting: the drone bed, the ambience and the thruster are
   * loops that run forever, and a suspended context stops rendering them
   * altogether rather than rendering silence. `resume` is already safe to call
   * on every gesture, so coming back needs nothing new.
   */
  suspend() {
    if (this.ctx && this.ctx.state === 'running') {
      this.ctx.suspend().catch(() => { /* it will be resumed on the way back */ });
    }
  }

  /** Call from a user gesture — browsers won't start audio otherwise. */
  start() {
    if (this.started) {
      this.resume();
      return;
    }
    this.started = true;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor();
    this.ctx = ctx;

    this.master = ctx.createGain();
    // Honours a mute restored from settings before any gesture existed.
    this.master.gain.value = this.muted ? 0 : Audio.LEVEL;

    // Everything underwater loses its top end.
    this.muffle = ctx.createBiquadFilter();
    this.muffle.type = 'lowpass';
    this.muffle.frequency.value = 1350;
    this.muffle.Q.value = 0.5;

    this.reverb = ctx.createConvolver();
    this.reverb.buffer = this.makeImpulse(ctx, 3.4, 2.4);
    this.wet = ctx.createGain();
    this.wet.gain.value = 0.34;

    // Everything diegetic lands on the effects bus; the score and the water hum
    // reach master by their own routes, so all three can be set independently.
    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = this.vol.sfx;
    this.sfxBus.connect(this.master);

    this.muffle.connect(this.sfxBus);
    this.muffle.connect(this.wet);
    this.wet.connect(this.reverb);
    this.reverb.connect(this.sfxBus);
    this.master.connect(ctx.destination);

    // Straight to master, deliberately bypassing the muffle — see music.ts.
    this.music = new Music(ctx, this.master);
    this.music.level = 0.55 * this.vol.music;
    // The graph now exists, so a mute that was only a flag until this moment
    // can finally reach it — including the copy `Music` keeps for itself.
    this.applyMute();

    this.noiseBuffer = this.makeNoise(ctx, 2.5);
    // Created inside a gesture it is usually already running; if not, ask.
    if (ctx.state !== 'running') ctx.resume().catch(() => {});

    // --- thruster: filtered noise, always running, gain follows throttle ---
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    this.thrusterFilter = ctx.createBiquadFilter();
    this.thrusterFilter.type = 'bandpass';
    this.thrusterFilter.frequency.value = 140;
    this.thrusterFilter.Q.value = 2.2;
    this.thrusterGain = ctx.createGain();
    this.thrusterGain.gain.value = 0;
    src.connect(this.thrusterFilter);
    this.thrusterFilter.connect(this.thrusterGain);
    this.thrusterGain.connect(this.muffle);
    src.start();

    // --- ambience: a slow, very low bed ---
    const amb = ctx.createBufferSource();
    amb.buffer = this.noiseBuffer;
    amb.loop = true;
    const ambFilter = ctx.createBiquadFilter();
    ambFilter.type = 'lowpass';
    ambFilter.frequency.value = 190;
    this.ambienceGain = ctx.createGain();
    this.ambienceGain.gain.value = 0.16;
    amb.connect(ambFilter);
    ambFilter.connect(this.ambienceGain);
    this.ambienceGain.connect(this.master);
    amb.start();

    // --- music: detuned drones, silent until something happens ---
    this.musicGain = ctx.createGain();
    this.musicGain.gain.value = 0;
    // A second low-pass in front of the bed only. The mood moves this, not the
    // master muffle, so events keep their own brightness while the drone shifts.
    this.musicTone = ctx.createBiquadFilter();
    this.musicTone.type = 'lowpass';
    this.musicTone.frequency.value = MOODS.calm.tone;
    this.musicTone.Q.value = MOODS.calm.q;
    this.musicGain.connect(this.musicTone);
    this.musicTone.connect(this.muffle);

    // Bed-only send into the room, on top of whatever the master send does.
    this.musicWet = ctx.createGain();
    this.musicWet.gain.value = 0;
    this.musicTone.connect(this.musicWet);
    this.musicWet.connect(this.reverb);

    // And a long tail for the enormous moods. A second convolver would cost
    // more than a phone can spare, so this is two feedback delays through a
    // dark filter — from the inside of a drone it reads the same. Loop gain has
    // to stay under 1: both delays are re-fed, so the ceiling is 0.5, not 1.
    this.spaceWet = ctx.createGain();
    this.spaceWet.gain.value = 0;
    const dA = ctx.createDelay(1.5);
    dA.delayTime.value = 0.37;
    const dB = ctx.createDelay(1.5);
    dB.delayTime.value = 0.53;
    const tailLp = ctx.createBiquadFilter();
    tailLp.type = 'lowpass';
    tailLp.frequency.value = 520;
    const tailFb = ctx.createGain();
    tailFb.gain.value = 0.3;
    this.musicTone.connect(this.spaceWet);
    this.spaceWet.connect(dA);
    this.spaceWet.connect(dB);
    dA.connect(tailLp);
    dB.connect(tailLp);
    tailLp.connect(tailFb);
    tailFb.connect(dA);
    tailFb.connect(dB);
    tailLp.connect(this.master);

    const calm = MOODS.calm;
    for (let i = 0; i < 6; i++) {
      const osc = ctx.createOscillator();
      // Sines everywhere except the shimmer partial, which wants a little edge.
      osc.type = i === 4 ? 'triangle' : 'sine';
      osc.frequency.value = calm.freqs[i];
      osc.detune.value = calm.detune[i];
      const g = ctx.createGain();
      g.gain.value = 0.22 * calm.levels[i];
      osc.connect(g);
      g.connect(this.musicGain);
      osc.start();
      this.musicVoices.push({ osc, gain: g });
    }
  }

  private makeNoise(ctx: AudioContext, seconds: number): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      // Cheap pink-ish filtering — brown noise reads as "deep water".
      b0 = 0.997 * b0 + w * 0.0555;
      b1 = 0.963 * b1 + w * 0.0750;
      b2 = 0.570 * b2 + w * 0.1538;
      d[i] = (b0 + b1 + b2 + w * 0.018) * 0.9;
    }
    return buf;
  }

  private makeImpulse(ctx: AudioContext, seconds: number, decay: number): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) {
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
      }
    }
    return buf;
  }

  private now() {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  /** Distance attenuation so events far from the sub stay in the background. */
  private falloff(dist: number, reach = 180) {
    return clamp01(1 - dist / reach) ** 1.6;
  }

  private burst(opts: {
    freq: number; endFreq?: number; dur: number; gain: number;
    type?: OscillatorType; noise?: boolean; q?: number; delay?: number;
  }) {
    const ctx = this.ctx;
    if (!ctx || this.muted || opts.gain <= 0.0005) return;
    const t = this.now() + (opts.delay ?? 0);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, opts.gain), t + Math.min(0.02, opts.dur * 0.2));
    g.gain.exponentialRampToValueAtTime(0.0001, t + opts.dur);
    g.connect(this.muffle);

    this.voices++;
    if (this.voices > this.voicePeak) this.voicePeak = this.voices;

    if (opts.noise && this.noiseBuffer) {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuffer;
      src.playbackRate.value = 0.6 + Math.random() * 0.8;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.Q.value = opts.q ?? 1.1;
      f.frequency.setValueAtTime(opts.freq, t);
      f.frequency.exponentialRampToValueAtTime(Math.max(30, opts.endFreq ?? opts.freq), t + opts.dur);
      src.connect(f);
      f.connect(g);
      src.start(t);
      src.stop(t + opts.dur + 0.05);
      src.onended = () => this.release(src, f, g);
    } else {
      const osc = ctx.createOscillator();
      osc.type = opts.type ?? 'sine';
      osc.frequency.setValueAtTime(opts.freq, t);
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, opts.endFreq ?? opts.freq), t + opts.dur);
      osc.connect(g);
      osc.start(t);
      osc.stop(t + opts.dur + 0.05);
      osc.onended = () => this.release(osc, null, g);
    }
  }

  /**
   * Unwire a finished voice.
   *
   * A source node stops on its own, but the gain it feeds stays connected to
   * `muffle`, and a node reachable from the destination is a node the audio
   * thread renders every 128-sample quantum whether or not anything is going
   * through it. Nothing here ever disconnected: `burst` is on every shot, hit
   * and pickup, and `arp` calls it twice a note, so the graph only ever grew.
   * That thread runs at real-time priority, so what it wastes comes off the
   * frame rather than out of the audio.
   */
  private release(src: AudioScheduledSourceNode, mid: BiquadFilterNode | null, g: GainNode) {
    src.onended = null;
    src.disconnect();
    mid?.disconnect();
    g.disconnect();
    this.voices--;
  }

  /** Semitones from A3. Everything tuned lives on this so pickups agree. */
  private hz(semi: number) {
    return 220 * Math.pow(2, semi / 12);
  }

  /**
   * A short figure in A minor. Notes ring into each other rather than stopping,
   * which is what stops a fast string of pickups turning into a rattle.
   */
  private arp(semis: readonly number[], opts: {
    gain: number; step?: number; dur?: number; type?: OscillatorType; shimmer?: number;
  }) {
    const step = opts.step ?? 0.075;
    const dur = opts.dur ?? 0.34;
    const shimmer = opts.shimmer ?? 0.3;
    for (let i = 0; i < semis.length; i++) {
      const f = this.hz(semis[i]);
      this.burst({ freq: f, dur, gain: opts.gain, type: opts.type ?? 'triangle', delay: i * step });
      // An octave above at a whisper — the master muffle eats the fundamental
      // otherwise and pickups stop cutting through a fight.
      this.burst({ freq: f * 2, dur: dur * 0.55, gain: opts.gain * shimmer, type: 'sine', delay: i * step + 0.012 });
    }
  }

  // --- events ---------------------------------------------------------------

  cannon(dist = 0) {
    const a = this.falloff(dist, 220) * 0.34;
    this.burst({ freq: 420, endFreq: 90, dur: 0.16, gain: a, noise: true, q: 0.8 });
    this.burst({ freq: 150, endFreq: 60, dur: 0.12, gain: a * 0.7, type: 'square' });
  }

  torpedo(dist = 0) {
    const a = this.falloff(dist, 260) * 0.5;
    this.burst({ freq: 900, endFreq: 220, dur: 0.55, gain: a * 0.7, noise: true, q: 0.5 });
    this.burst({ freq: 90, endFreq: 45, dur: 0.5, gain: a });
  }

  explosion(dist = 0, scale = 1) {
    const a = this.falloff(dist, 420) * 0.85 * Math.min(2, scale);
    this.burst({ freq: 260, endFreq: 38, dur: 0.9 * scale, gain: a, noise: true, q: 0.4 });
    this.burst({ freq: 62, endFreq: 24, dur: 1.5 * scale, gain: a * 0.9, type: 'sine' });
    this.burst({ freq: 1400, endFreq: 300, dur: 0.25, gain: a * 0.35, noise: true, q: 1.4, delay: 0.01 });
  }

  /** A hull-sized sheet of water, asymmetric on exit and re-entry. */
  splash(speed: number, out: boolean) {
    const k = clamp01((speed - 1.2) / 10.5);
    const a = (0.22 + k * 0.52) * (out ? 0.82 : 1.0);
    // The crack is the aerated surface breaking; the broad band is the mass of
    // water moving, and the low pulse makes a hard re-entry land in the body.
    this.burst({
      freq: out ? 1500 : 1180, endFreq: out ? 330 : 190,
      dur: out ? 0.34 : 0.52, gain: a * 0.72, noise: true, q: 0.72,
    });
    this.burst({
      freq: out ? 360 : 280, endFreq: 58,
      dur: out ? 0.54 : 0.82, gain: a, noise: true, q: 0.42,
    });
    if (!out) {
      this.burst({ freq: 72, endFreq: 34, dur: 0.62, gain: a * 0.48, type: 'sine' });
    }
  }

  ping() {
    // The classic two-tone active pulse, with a long tail.
    this.burst({ freq: 1500, endFreq: 1480, dur: 0.16, gain: 0.34, type: 'sine' });
    this.burst({ freq: 1120, endFreq: 900, dur: 0.9, gain: 0.22, type: 'sine', delay: 0.11 });
  }

  impact(force: number) {
    const a = clamp01(force / 22) * 0.6;
    this.burst({ freq: 220, endFreq: 55, dur: 0.35, gain: a, noise: true, q: 0.7 });
    this.burst({ freq: 74, endFreq: 40, dur: 0.5, gain: a * 0.8 });
  }

  scrape(force: number) {
    this.burst({ freq: 1600, endFreq: 700, dur: 0.12, gain: clamp01(force / 14) * 0.12, noise: true, q: 3.4 });
  }

  creak(depth: number) {
    const a = 0.1 + clamp01(depth / 900) * 0.22;
    this.burst({ freq: 130 + Math.random() * 140, endFreq: 70, dur: 0.9 + Math.random(), gain: a, noise: true, q: 6 });
  }

  pickup() {
    this.burst({ freq: 640, endFreq: 1280, dur: 0.22, gain: 0.24, type: 'triangle' });
    this.burst({ freq: 960, endFreq: 1920, dur: 0.3, gain: 0.14, type: 'sine', delay: 0.07 });
  }

  alarm() {
    this.burst({ freq: 760, endFreq: 560, dur: 0.3, gain: 0.2, type: 'sawtooth' });
  }

  creature(kind: 'wake' | 'strike' | 'hurt' | 'death', dist = 0) {
    const a = this.falloff(dist, 460);
    switch (kind) {
      case 'wake':
        this.burst({ freq: 74, endFreq: 42, dur: 2.6, gain: 0.5 * a, type: 'sine' });
        this.burst({ freq: 190, endFreq: 80, dur: 2.2, gain: 0.3 * a, noise: true, q: 2.4 });
        break;
      case 'strike':
        this.burst({ freq: 300, endFreq: 60, dur: 0.7, gain: 0.55 * a, noise: true, q: 1.1 });
        break;
      case 'hurt':
        this.burst({ freq: 420, endFreq: 120, dur: 0.9, gain: 0.5 * a, type: 'sawtooth' });
        break;
      case 'death':
        this.burst({ freq: 120, endFreq: 30, dur: 4.5, gain: 0.7 * a, type: 'sine' });
        this.burst({ freq: 500, endFreq: 60, dur: 2.4, gain: 0.4 * a, noise: true, q: 0.8 });
        break;
    }
  }

  distantLife() {
    // Faint, unplaceable calls — the ocean is inhabited (PRD §28).
    const f = 90 + Math.random() * 220;
    this.burst({ freq: f, endFreq: f * (0.5 + Math.random() * 0.4), dur: 1.2 + Math.random() * 2.2, gain: 0.055, type: 'sine' });
  }

  // --- nitro ----------------------------------------------------------------

  /** Built on first use, then kept running forever at zero gain. */
  private buildNitro() {
    const ctx = this.ctx;
    if (!ctx || this.nitroGain || !this.noiseBuffer) return;

    const g = ctx.createGain();
    g.gain.value = 0;
    g.connect(this.muffle);

    // The roar: fast noise through a wide band, the water tearing past.
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    src.playbackRate.value = 1.6;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 240;
    band.Q.value = 0.8;
    src.connect(band);
    band.connect(g);

    // Under it, a saw that climbs as the jet spools up. Filtered hard — a raw
    // saw sounds like a lawnmower, and nothing sounds like that down here.
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = 62;
    const low = ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.value = 380;
    low.Q.value = 3.2;
    const og = ctx.createGain();
    og.gain.value = 0.55;
    osc.connect(low);
    low.connect(og);
    og.connect(g);

    src.start();
    osc.start();
    this.nitroGain = g;
    this.nitroOsc = osc;
    this.nitroBand = band;
    this.nitroLow = low;
  }

  /** Re-applies the jet state. Cheap enough to run every frame. */
  private applyNitro(transition: boolean) {
    if (!this.ctx) return;
    const fresh = !this.nitroGain;
    if (this.nitroOn) this.buildNitro();
    const g = this.nitroGain;
    if (!g) return;
    const t = this.now();
    const on = this.nitroOn && !this.muted;
    // Attack faster than the release; a jet that dies instantly reads as a bug.
    g.gain.setTargetAtTime(on ? 0.17 : 0, t, this.nitroOn ? 0.09 : 0.24);
    if (transition || (fresh && this.nitroOn)) {
      this.nitroOsc?.frequency.setTargetAtTime(this.nitroOn ? 205 : 62, t, this.nitroOn ? 0.45 : 0.3);
      this.nitroBand?.frequency.setTargetAtTime(this.nitroOn ? 520 : 240, t, 0.35);
      this.nitroLow?.frequency.setTargetAtTime(this.nitroOn ? 900 : 380, t, 0.5);
    }
  }

  /**
   * Start/stop the nitro jet. Safe to call with the same value every frame —
   * only a genuine flip touches the graph, so voices never stack up.
   */
  nitro(on: boolean) {
    if (on === this.nitroOn) return;
    this.nitroOn = on;
    this.applyNitro(true);
  }

  /** The tank coughing on empty: pressure out, nothing behind it. */
  nitroEmpty() {
    this.burst({ freq: 520, endFreq: 90, dur: 0.22, gain: 0.16, noise: true, q: 1.6 });
    this.burst({ freq: 150, endFreq: 62, dur: 0.3, gain: 0.1, type: 'sine', delay: 0.03 });
    this.burst({ freq: 340, endFreq: 120, dur: 0.14, gain: 0.06, noise: true, q: 3.2, delay: 0.16 });
  }

  // --- shields --------------------------------------------------------------

  /** A ringing zap off the bubble; harder hits ring higher. */
  shieldHit(strength: number) {
    const s = clamp01(strength);
    const f = 620 + s * 700;
    const a = 0.14 + s * 0.16;
    this.burst({ freq: f, endFreq: f * 0.94, dur: 0.28 + s * 0.2, gain: a, type: 'sine' });
    this.burst({ freq: f * 1.5, endFreq: f * 1.4, dur: 0.16, gain: a * 0.4, type: 'triangle', delay: 0.008 });
    this.burst({ freq: f * 2.1, endFreq: f * 0.8, dur: 0.12, gain: a * 0.3, noise: true, q: 7 });
  }

  /** Glass going, then the field collapsing down and out. Unmistakably bad. */
  shieldBreak() {
    for (let i = 0; i < 5; i++) {
      // Inharmonic partials, staggered — that irregularity is what reads as glass.
      const f = 900 + Math.random() * 1400;
      this.burst({ freq: f, endFreq: f * 0.9, dur: 0.18 + Math.random() * 0.2, gain: 0.09, type: 'sine', delay: i * 0.026 });
    }
    this.burst({ freq: 1800, endFreq: 300, dur: 0.35, gain: 0.2, noise: true, q: 2.6, delay: 0.02 });
    this.burst({ freq: 700, endFreq: 60, dur: 1.2, gain: 0.26, type: 'sawtooth', delay: 0.12 });
    this.burst({ freq: 90, endFreq: 34, dur: 1.4, gain: 0.18, type: 'sine', delay: 0.14 });
  }

  // --- pickups and menus ----------------------------------------------------

  /**
   * One figure per pickup, all in A minor, so grabbing a handful in a second
   * sounds like a phrase instead of a keyboard falling down the stairs.
   * Salvage is the smallest thing here; life is the biggest.
   */
  powerup(kind: PowerupKind) {
    switch (kind) {
      case 'salvage': // the small change of the ocean floor
        this.arp([7, 12], { gain: 0.09, step: 0.05, dur: 0.17, shimmer: 0.22 });
        break;
      case 'repair': // A C E — warm, resolved, nothing clever
        this.arp([0, 3, 7], { gain: 0.15, dur: 0.4 });
        this.burst({ freq: this.hz(-12), dur: 0.7, gain: 0.09, type: 'sine' });
        break;
      case 'shield':
        this.arp([3, 7, 12], { gain: 0.14, step: 0.065, dur: 0.42, type: 'sine', shimmer: 0.45 });
        break;
      case 'energy':
        this.arp([0, 7, 14], { gain: 0.13, step: 0.06, dur: 0.36, type: 'sine', shimmer: 0.5 });
        break;
      case 'nitro': // straight up, like the tank filling
        this.arp([5, 12, 17], { gain: 0.13, step: 0.05, dur: 0.3 });
        break;
      case 'torpedo': // sits low, because that is where the weight is
        this.arp([-5, 0, 7], { gain: 0.15, step: 0.07, dur: 0.45, shimmer: 0.2 });
        break;
      case 'charge':
        this.arp([0, 5, 7, 12], { gain: 0.13, step: 0.06, dur: 0.4 });
        break;
      case 'overdrive': // the one with teeth
        this.arp([0, 7, 12, 19], { gain: 0.13, step: 0.055, dur: 0.42, type: 'sawtooth', shimmer: 0.25 });
        this.burst({ freq: this.hz(-12), endFreq: this.hz(-5), dur: 0.8, gain: 0.11, type: 'sine' });
        break;
      case 'aegis':
        this.arp([3, 10, 15, 19], { gain: 0.13, step: 0.07, dur: 0.5, type: 'sine', shimmer: 0.5 });
        this.burst({ freq: this.hz(-9), dur: 1.1, gain: 0.1, type: 'sine' });
        break;
      case 'module':
        this.arp([2, 7, 14, 19], { gain: 0.12, step: 0.055, dur: 0.38 });
        break;
      case 'life': // the biggest thing in the list — five notes and a floor under them
        this.arp([0, 7, 12, 15, 19], { gain: 0.16, step: 0.085, dur: 0.75, shimmer: 0.45 });
        this.burst({ freq: this.hz(-24), dur: 1.8, gain: 0.14, type: 'sine' });
        this.burst({ freq: this.hz(-12), dur: 1.5, gain: 0.1, type: 'sine', delay: 0.09 });
        break;
    }
  }

  /** Money spent well: the whole chord at once, with a low root under it. */
  upgradeBought() {
    this.arp([0, 7, 12, 15], { gain: 0.14, step: 0.018, dur: 0.9, shimmer: 0.4 });
    this.burst({ freq: this.hz(-12), dur: 1.3, gain: 0.13, type: 'sine' });
    this.burst({ freq: this.hz(19), dur: 0.5, gain: 0.05, type: 'sine', delay: 0.14 });
  }

  /** Clamps on: a warm two-note motif. Off: one soft blip and go. */
  dock(active: boolean) {
    if (active) {
      this.burst({ freq: this.hz(-5), dur: 0.6, gain: 0.16, type: 'triangle' });
      this.burst({ freq: this.hz(0), dur: 1.1, gain: 0.14, type: 'triangle', delay: 0.24 });
      this.burst({ freq: this.hz(-17), dur: 1.4, gain: 0.12, type: 'sine' });
      this.burst({ freq: 180, endFreq: 90, dur: 0.5, gain: 0.07, noise: true, q: 1.4, delay: 0.02 });
    } else {
      this.burst({ freq: this.hz(0), endFreq: this.hz(-5), dur: 0.3, gain: 0.1, type: 'sine' });
      this.burst({ freq: 220, endFreq: 110, dur: 0.24, gain: 0.05, noise: true, q: 2 });
    }
  }

  /** A tick. Nothing more — menus should not be a performance. */
  menuMove() {
    this.burst({ freq: 900, endFreq: 700, dur: 0.05, gain: 0.06, type: 'sine' });
  }

  menuConfirm() {
    this.burst({ freq: this.hz(7), dur: 0.12, gain: 0.11, type: 'triangle' });
    this.burst({ freq: this.hz(12), dur: 0.2, gain: 0.08, type: 'sine', delay: 0.055 });
  }

  menuBack() {
    this.burst({ freq: this.hz(5), dur: 0.12, gain: 0.1, type: 'triangle' });
    this.burst({ freq: this.hz(-2), dur: 0.22, gain: 0.08, type: 'sine', delay: 0.055 });
  }

  // --- hostiles -------------------------------------------------------------

  /** Station guns: flatter and harder than ours, and it should be obvious. */
  turretFire(dist = 0) {
    const a = this.falloff(dist, 240) * 0.3;
    this.burst({ freq: 640, endFreq: 380, dur: 0.09, gain: a, noise: true, q: 2.4 });
    this.burst({ freq: 190, endFreq: 150, dur: 0.1, gain: a * 0.8, type: 'square' });
    this.burst({ freq: 84, endFreq: 60, dur: 0.22, gain: a * 0.5, type: 'sine', delay: 0.01 });
  }

  /** Someone else's sonar. A semitone under ours, and made of metal. */
  hunterPing(dist = 0) {
    const a = this.falloff(dist, 700) * 0.9 + 0.06;
    this.burst({ freq: 1416, endFreq: 1400, dur: 0.15, gain: 0.3 * a, type: 'square' });
    this.burst({ freq: 1998, endFreq: 1980, dur: 0.11, gain: 0.08 * a, type: 'sine' });
    this.burst({ freq: 1057, endFreq: 840, dur: 0.95, gain: 0.2 * a, type: 'sine', delay: 0.11 });
    this.burst({ freq: 2100, endFreq: 1400, dur: 0.5, gain: 0.05 * a, noise: true, q: 9, delay: 0.11 });
  }

  /** The lurker closing its mouth. Wet first, then the crunch. */
  bite(dist = 0) {
    const a = this.falloff(dist, 300);
    this.burst({ freq: 900, endFreq: 260, dur: 0.09, gain: 0.3 * a, noise: true, q: 1.2 });
    this.burst({ freq: 260, endFreq: 70, dur: 0.22, gain: 0.34 * a, noise: true, q: 0.7, delay: 0.03 });
    this.burst({ freq: 110, endFreq: 44, dur: 0.4, gain: 0.26 * a, type: 'sine', delay: 0.03 });
    this.burst({ freq: 1500, endFreq: 600, dur: 0.07, gain: 0.1 * a, noise: true, q: 5, delay: 0.045 });
  }

  /** The capital ship. Sub-bass you feel, and its own hull failing over it. */
  bossRoar(phase: 'wake' | 'rage' | 'die', dist = 0) {
    const a = this.falloff(dist, 900) * 0.8 + 0.12;
    switch (phase) {
      case 'wake':
        this.burst({ freq: 42, endFreq: 29, dur: 4.2, gain: 0.6 * a, type: 'sine' });
        this.burst({ freq: 63, endFreq: 44, dur: 3.4, gain: 0.22 * a, type: 'triangle', delay: 0.3 });
        this.burst({ freq: 320, endFreq: 180, dur: 2.6, gain: 0.14 * a, noise: true, q: 8, delay: 0.5 });
        break;
      case 'rage':
        this.burst({ freq: 58, endFreq: 36, dur: 2.2, gain: 0.7 * a, type: 'sine' });
        this.burst({ freq: 87, endFreq: 55, dur: 1.8, gain: 0.3 * a, type: 'sawtooth' });
        this.burst({ freq: 460, endFreq: 240, dur: 1.2, gain: 0.2 * a, noise: true, q: 6, delay: 0.06 });
        this.burst({ freq: 240, endFreq: 190, dur: 1.6, gain: 0.12 * a, noise: true, q: 11, delay: 0.2 });
        break;
      case 'die':
        this.burst({ freq: 48, endFreq: 18, dur: 6.5, gain: 0.75 * a, type: 'sine' });
        this.burst({ freq: 300, endFreq: 90, dur: 4.5, gain: 0.24 * a, noise: true, q: 4, delay: 0.2 });
        // Plate after plate letting go on the way down.
        for (let i = 0; i < 5; i++) {
          this.burst({
            freq: 500 + Math.random() * 700, endFreq: 120, dur: 0.5 + Math.random(),
            gain: 0.13 * a, noise: true, q: 9, delay: 0.4 + i * 0.55 + Math.random() * 0.3,
          });
        }
        break;
    }
  }

  /** Terrain coming apart: the crack, then everything that was holding it up. */
  rockBreak(dist = 0) {
    const a = this.falloff(dist, 340);
    this.burst({ freq: 200, endFreq: 52, dur: 0.4, gain: 0.34 * a, noise: true, q: 0.9 });
    this.burst({ freq: 70, endFreq: 34, dur: 0.7, gain: 0.28 * a, type: 'sine' });
    // Rubble — a handful of short grains, scattered in time and pitch.
    for (let i = 0; i < 4; i++) {
      this.burst({
        freq: 600 + Math.random() * 900, endFreq: 180, dur: 0.09 + Math.random() * 0.13,
        gain: 0.09 * a, noise: true, q: 4.5, delay: 0.06 + Math.random() * 0.7,
      });
    }
  }

  // --- warnings -------------------------------------------------------------

  /** One cycle of the hull alarm. Driven by the timer in update(). */
  private lowHullBeep() {
    this.burst({ freq: 330, endFreq: 320, dur: 0.32, gain: 0.15, type: 'sawtooth' });
    this.burst({ freq: 247, endFreq: 240, dur: 0.42, gain: 0.13, type: 'sawtooth', delay: 0.3 });
    this.burst({ freq: 62, endFreq: 46, dur: 0.6, gain: 0.08, type: 'sine' });
  }

  /**
   * The hull warning. Like nitro(), a state and not an event — the repeat is a
   * timer in update(), so calling this every frame costs a comparison.
   */
  lowHull(on: boolean) {
    if (on === this.lowHullOn) return;
    this.lowHullOn = on;
    if (on) this.lowHullTimer = 0; // sound on the next frame, not in two seconds
  }

  /** The occasional noise a mood makes on its own, between the drones. */
  private moodEvent(m: Mood, intensity: number) {
    const a = 0.55 + clamp01(intensity) * 0.45;
    switch (m) {
      case 'calm':
        break;
      case 'kelp': // fronds moving across each other
        this.burst({ freq: 520, endFreq: 240, dur: 1.4 + Math.random(), gain: 0.035, noise: true, q: 2.4 });
        break;
      case 'city': { // a far-off knock on something hollow and still standing
        const f = this.hz([0, 7, 12][Math.floor(Math.random() * 3)]);
        this.burst({ freq: f, endFreq: f * 0.98, dur: 2.4, gain: 0.05, type: 'sine' });
        this.burst({ freq: 180, endFreq: 90, dur: 0.5, gain: 0.03, noise: true, q: 5 });
        break;
      }
      case 'military': // a gun deck idling
        this.burst({ freq: 62, endFreq: 44, dur: 0.5, gain: 0.08 * a, type: 'sine' });
        this.burst({ freq: 250, endFreq: 150, dur: 0.11, gain: 0.022 * a, noise: true, q: 3.4 });
        break;
      case 'vents':
        this.burst({
          freq: 70 + Math.random() * 70, endFreq: 30, dur: 1.4 + Math.random() * 1.8,
          gain: 0.085 * a, noise: true, q: 0.6,
        });
        break;
      case 'abyss': // something enormous, a long way off, not coming closer
        this.burst({ freq: 34, endFreq: 21, dur: 4.5, gain: 0.1 * a, type: 'sine' });
        break;
      case 'ancient': { // a bell, inharmonic partial and all
        const f = this.hz([0, 7, 12, 19][Math.floor(Math.random() * 4)]);
        this.burst({ freq: f, dur: 3.2, gain: 0.05, type: 'sine' });
        this.burst({ freq: f * 2.76, dur: 1.3, gain: 0.016, type: 'sine' });
        break;
      }
      case 'boss': // lub-dub
        this.burst({ freq: 48, endFreq: 30, dur: 0.5, gain: 0.11 * a, type: 'sine' });
        this.burst({ freq: 40, endFreq: 26, dur: 0.7, gain: 0.08 * a, type: 'sine', delay: 0.17 });
        break;
    }
  }

  /** Per-frame mix update. `intensity` drives music fade-in (PRD §29). */
  update(dt: number, s: {
    throttle: number; speed: number; depth: number;
    intensity: number; boost: boolean;
    /** Where we are. Omit it and the bed stays exactly as it always was. */
    mood?: Mood;
  }) {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = this.now();
    const inten = clamp01(s.intensity);

    const thrust = clamp01(s.throttle) * (s.boost ? 1.35 : 1);
    this.thrusterGain.gain.setTargetAtTime(this.muted ? 0 : 0.035 + thrust * 0.20, t, 0.12);
    this.thrusterFilter.frequency.setTargetAtTime(105 + thrust * 130 + s.speed * 2.4, t, 0.15);

    // The jet re-asserts itself every frame so mute and unmute both land on it.
    if (this.nitroOn || this.nitroGain) this.applyNitro(false);

    const mood = s.mood ?? 'calm';
    const spec = MOODS[mood];
    if (mood !== this.mood) {
      // A new region should announce itself reasonably soon, but not on the
      // frame we crossed the line — that would land on top of the crossfade.
      this.mood = mood;
      if (spec.every > 0) this.moodTimer = Math.min(this.moodTimer, 1.2 + spec.every * 0.5);
    }

    // Deeper water is quieter and darker.
    this.muffle.frequency.setTargetAtTime(1500 - Math.min(900, s.depth * 0.55), t, 0.6);
    this.ambienceGain.gain.setTargetAtTime(
      this.muted ? 0 : (0.16 - Math.min(0.1, s.depth * 0.00006)) * spec.amb * this.vol.amb, t, 1.5);

    // Music only rises for combat, discoveries and the boss. Everything mood-
    // shaped moves on a ~2s time constant, so a region change is a slow turn of
    // the harmony and never a cut — including the drone pitches, which glide.
    const MOOD_TC = 0.75;
    const duck = this.nitroOn ? 0.62 : 1; // the jet owns the mix while it burns
    // The drone bed is music, so it takes the music level too — it just happens
    // to reach master through the muffle, because it IS meant to sound like it
    // is underwater in a way the score is not.
    this.musicGain.gain.setTargetAtTime(
      this.muted ? 0 : inten * 0.16 * spec.bed * duck * this.vol.music, t, 1.8);
    // The drone bed rises with intensity and the score falls with it, so the
    // two hand over instead of piling up: exploration is scored, a fight is not.
    this.music?.duck(inten);
    this.musicTone.frequency.setTargetAtTime(spec.tone, t, MOOD_TC);
    this.musicTone.Q.setTargetAtTime(spec.q, t, MOOD_TC);
    this.musicWet.gain.setTargetAtTime(spec.wet, t, MOOD_TC);
    this.spaceWet.gain.setTargetAtTime(spec.space, t, MOOD_TC);
    for (let i = 0; i < this.musicVoices.length; i++) {
      const v = this.musicVoices[i];
      v.gain.gain.setTargetAtTime((0.1 + inten * 0.2) * spec.levels[i], t, 2.2);
      v.osc.frequency.setTargetAtTime(spec.freqs[i], t, MOOD_TC);
      v.osc.detune.setTargetAtTime(spec.detune[i], t, MOOD_TC);
    }
    // Vents refuse to sit still.
    if (mood === 'vents') {
      this.musicTone.frequency.setTargetAtTime(spec.tone * (0.7 + Math.random() * 0.7), t, 0.4);
    }

    // Whatever the mood does between drones — a pulse, a bell, a rumble.
    if (spec.every > 0) {
      this.moodTimer -= dt;
      if (this.moodTimer <= 0) {
        this.moodTimer = spec.every * (0.7 + Math.random() * 0.6);
        if (!this.muted) this.moodEvent(mood, inten);
      }
    } else {
      this.moodTimer = 1;
    }

    if (this.lowHullOn) {
      this.lowHullTimer -= dt;
      if (this.lowHullTimer <= 0) {
        this.lowHullTimer = 1.9;
        if (!this.muted) this.lowHullBeep();
      }
    }

    // Hull creaks get more frequent with depth.
    this.creakTimer -= dt * (0.5 + Math.min(2.5, s.depth / 400));
    if (this.creakTimer <= 0) {
      this.creakTimer = 3 + Math.random() * 9;
      if (!this.muted) this.creak(s.depth);
    }

    this.ambientTimer -= dt;
    if (this.ambientTimer <= 0) {
      this.ambientTimer = 9 + Math.random() * 22;
      if (!this.muted && Math.random() < 0.7) this.distantLife();
    }
  }

  /** Apply the player's bus levels. Safe before the context exists. */
  setLevels(v: { music: number; sfx: number; amb: number }) {
    this.vol = v;
    if (!this.ctx) return;
    this.sfxBus.gain.setTargetAtTime(v.sfx, this.now(), 0.08);
    if (this.music) this.music.level = 0.55 * v.music;
  }

  /**
   * Mute is a STATE, and this is the only thing that sets it.
   *
   * It used to be a bare toggle, with the truth duplicated across `settings`,
   * the HUD and this flag, each flipped by hand at its own call site. Three
   * copies of a boolean flipped in three places drift the first time one path
   * runs without the others, and then the menu says one thing while the graph
   * does another. Callers now say what they want rather than asking for the
   * opposite of whatever happens to be true.
   */
  setMuted(on: boolean) {
    this.muted = on;
    this.applyMute();
    return this.muted;
  }

  toggleMute() {
    return this.setMuted(!this.muted);
  }

  /**
   * Push the flag into the graph. Called on every change AND at the end of
   * `start`, because the graph does not exist until a user gesture builds it:
   * a mute restored from settings at boot lands while `ctx` is still null, and
   * `start` then brought the master up to full without ever consulting it.
   * Sound effects and the drone bed check `muted` on their own so they stayed
   * silent, but the music does not — it has its own gain and hangs off master
   * — so it alone came back, which is exactly how this was noticed.
   */
  private applyMute() {
    if (this.ctx) this.master.gain.setTargetAtTime(this.muted ? 0 : Audio.LEVEL, this.now(), 0.1);
    // `Music` keeps its own mute; nothing had ever told it.
    this.music?.setMuted(this.muted);
  }
}
